import React from "react";
import { authService } from "../../services/auth";
import logoMhd from "../../img/logo-mhd.png";

interface SidebarProps {
  activePage: string;
  onPageChange: (page: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: (collapsed: boolean) => void;
  onLogout?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  activePage,
  onPageChange,
  isCollapsed,
  onToggleCollapse,
  onLogout,
}) => {
  // Get current user role
  const userRole = authService.getUserRole();
  const isAdmin = authService.isAdmin();

  // Define all menu items with role requirements
  const allMenuItems = [
    {
      id: "dashboard",
      name: "Dashboard tổng quan",
      roles: ["Admin"], // Only Admin can see Dashboard
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      id: "code-management",
      name: "Quản lý Code",
      roles: ["Admin", "User"], // Both Admin and User can see
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <rect x="4" y="4" width="6" height="6" rx="1" strokeWidth={1.5} />
          <rect x="14" y="4" width="6" height="6" rx="1" strokeWidth={1.5} />
          <rect x="4" y="14" width="6" height="6" rx="1" strokeWidth={1.5} />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M14 17h6m-3-3v6"
          />
        </svg>
      ),
    },
    {
      id: "campaigns",
      name: "Quản lý Chiến dịch",
      roles: ["Admin", "User"], // Both Admin and User can see
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
      ),
    },

    // {
    //   id: "campaign-conversion",
    //   name: "Chuyển đổi Chiến dịch",
    //   roles: ["Admin", "User"], // Both Admin and User can see
    //   icon: (
    //     <svg
    //       className="w-5 h-5"
    //       fill="none"
    //       stroke="currentColor"
    //       viewBox="0 0 24 24"
    //     >
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth={2}
    //         d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
    //       />
    //     </svg>
    //   ),
    //     },

    {
      id: "redemptions",
      name: "Quản lý Đổi quà",
      roles: ["Admin", "User"], // Both Admin and User can see
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    {
      id: "partners",
      name: "Quản lý Đối tác",
      roles: ["Admin", "User"], // Both Admin and User can see
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      ),
    },
    {
      id: "vouchers",
      name: "Quản lý Voucher",
      roles: ["Admin", "User"],
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
          />
        </svg>
      ),
    },
    {
      id: "user-management",
      name: "Quản lý Người dùng",
      roles: ["Admin"], // Only Admin can see User Management
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
    {
      id: "error-handling",
      name: "Xử lý thông báo",
      roles: ["Admin"], // Only Admin can see Error Handling
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
      ),
    },
    // {
    //   id: "test/verify-code",
    //   name: "Test Verify Code",
    //   roles: ["Admin"], // Only Admin can see Test page
    //   icon: (
    //     <svg
    //       className="w-5 h-5"
    //       fill="none"
    //       stroke="currentColor"
    //       viewBox="0 0 24 24"
    //     >
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth={2}
    //         d="M8 7V3m8 4V3M3 11h18M5 19h14a2 2 0 002-2v-6H3v6a2 2 0 002 2z"
    //       />
    //     </svg>
    //   ),
    // },
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter((item) => {
    if (!userRole) return false; // No role = no access
    return item.roles.includes(userRole);
  });

  const isMenuItemActive = (itemId: string) =>
    activePage === itemId || activePage.startsWith(`${itemId}/`);

  return (
    <aside
      className={`fixed left-0 top-0 h-full bg-white dark:bg-gray-900 border-r-2 border-gray-200 dark:border-gray-700 shadow-lg z-40 transition-all duration-300 ${
        isCollapsed ? "w-16" : "w-64"
      }`}
    >
      <div className="flex flex-col h-full">
        {/* Header với hamburger menu và LOGO - chỉ hiển thị LOGO khi chưa thu gọn */}
        <div className="p-[16px] border-b-2 border-gray-200 dark:border-gray-700">
          <div
            className={`flex items-center ${
              isCollapsed ? "justify-center" : "justify-between"
            }`}
          >
            {/* LOGO và tên công ty - chỉ hiển thị khi chưa thu gọn */}
            {!isCollapsed && (
              <div className="flex items-center space-x-3">
                <img
                  src={logoMhd}
                  alt="MHD Logo"
                  className="w-30 h-8 object-contain dark:brightness-0 dark:invert"
                />
              </div>
            )}

            {/* Hamburger menu button - 3 gạch ngang */}
            <button
              onClick={() => onToggleCollapse(!isCollapsed)}
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md transition-colors"
              title={isCollapsed ? "Mở rộng" : "Thu gọn"}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Menu chính */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => onPageChange(item.id)}
                  className={`w-full flex transition-all duration-300 ${
                    isCollapsed
                      ? `px-3 py-3 justify-center items-center ${
                          isMenuItemActive(item.id)
                            ? "text-blue-600 dark:text-blue-400"
                            : "text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400"
                        }`
                      : `px-4 py-3 items-start rounded-lg ${
                          isMenuItemActive(item.id)
                            ? "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30"
                            : "text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800"
                        }`
                  }`}
                  title={isCollapsed ? item.name : ""}
                >
                  <div
                    className={`transition-all duration-300 w-7 h-7 flex-shrink-0 ${
                      isCollapsed ? "" : "mt-0.5"
                    }`}
                  >
                    {item.icon}
                  </div>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      isCollapsed
                        ? "w-0 opacity-0 ml-0"
                        : "w-auto opacity-100 ml-3"
                    }`}
                  >
                    <span className="font-medium text-sm text-left leading-tight min-w-0 flex-1 break-words whitespace-nowrap">
                      {item.name}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer với nút đăng xuất */}
        <div className="p-4 bg-white dark:bg-gray-900">
          <div className="space-y-2">
            {/* Đăng xuất */}
            <button
              onClick={async () => {
                try {
                  await authService.logout();
                  if (onLogout) {
                    onLogout();
                  } else {
                    window.location.href = "/login";
                  }
                } catch (error) {
                  console.error("Logout failed:", error);
                  if (onLogout) {
                    onLogout();
                  } else {
                    window.location.href = "/login";
                  }
                }
              }}
              className={`w-full flex transition-all duration-300 rounded-lg ${
                isCollapsed
                  ? `px-3 py-3 justify-center text-red-500 dark:text-red-400`
                  : `px-4 py-3 items-start text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30`
              }`}
              title={isCollapsed ? "Đăng xuất" : ""}
            >
              <div
                className={`transition-all duration-300 w-6 h-6 flex-shrink-0 ${
                  isCollapsed ? "" : "mt-0.5"
                }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </div>
              <div
                className={`transition-all duration-300 overflow-hidden ${
                  isCollapsed ? "w-0 opacity-0 ml-0" : "w-auto opacity-100 ml-3"
                }`}
              >
                <span className="font-medium text-sm text-left leading-tight min-w-0 flex-1 break-words whitespace-nowrap">
                  Đăng xuất
                </span>
              </div>
            </button>

            {/* Icon cài đặt */}
            {/* <button
              className={`w-full flex transition-all duration-300 rounded-lg ${
                isCollapsed
                  ? `px-3 py-3 justify-center text-gray-600 hover:text-blue-500`
                  : `px-4 py-3 items-start text-gray-600 hover:text-blue-500 hover:bg-gray-50`
              }`}
              title={isCollapsed ? "Cài đặt" : ""}
            >
              <div
                className={`transition-all duration-300 w-6 h-6 flex-shrink-0 ${
                  isCollapsed ? "" : "mt-0.5"
                }`}
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <div
                className={`transition-all duration-300 overflow-hidden ${
                  isCollapsed ? "w-0 opacity-0 ml-0" : "w-auto opacity-100 ml-3"
                }`}
              >
                <span className="font-medium text-sm text-left leading-tight min-w-0 flex-1 break-words whitespace-nowrap">
                  Cài đặt
                </span>
              </div>
            </button> */}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
